<template>
    <!-- Celebratory gold-shimmer headline — the unified D8 + D9
         treatment shared between the speedtest "Complete!" screen and
         the survey "Thank you!" screen.

         `data-text` mirrors the headline string so the `::before`
         pseudo-element can re-render the word as a blurred,
         gold-clipped silhouette for the glyph-clipped glow halo (D8).
         The font-size sizing is consumer-tunable via the
         `--complete-headline-size` custom prop (each consumer hands a
         clamp expression scoped to its layout container). -->
    <h2 class="gold-headline" :data-text="text">{{ text }}</h2>
</template>

<script setup lang="ts">
/**
 * CompleteHeadline — the celebratory gold-shimmer headline primitive.
 *
 * Replaces the byte-identical `complete-headline-gold` (SpeedtestResults)
 * + `thank-you-headline-gold` (ThankYou) treatments that previously
 * lived twice — same gold gradient, same glyph-clipped `::before`
 * glow, same shimmer choreography, same PRM bracket. The two
 * consumers diverged only in (i) class names, (ii) keyframe name,
 * and (iii) font-size clamp.
 *
 * The shimmer keyframe is unified as `gold-headline-shimmer`. The
 * font-size hook is exposed via `--complete-headline-size` so each
 * consumer can tune the type register against its own container.
 *
 * Reads the shared `--color-gold` family + the `--motion-*` shimmer
 * tokens (canon at `styles/tokens.css` + glass-ui's tokens). The
 * `<h2>` only mounts on the consumer's `isComplete` / `shown` rising
 * edge (each consumer gates with its own `v-if` or transition), so
 * the one-shot shimmer fires exactly once per completion.
 */
defineProps<{
    /** Headline string — also mirrored to `data-text` for the glyph-clipped glow layer. */
    text: string;
}>();
</script>

<style scoped>
/* "Complete!" / "Thank you!" headline — the gold-shimmer celebratory
   treatment shared between the speedtest complete screen and the
   survey thank-you screen. Sizing is consumer-tunable via the
   `--complete-headline-size` custom prop so each container picks
   its own clamp register. */
.gold-headline {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: var(--complete-headline-size, clamp(2.25rem, 11cqi, 4rem));
    letter-spacing: -0.02em;
    line-height: 1;
    margin: 0;
    text-align: center;
    /* W12-β anticipation tick — before the gold-shimmer fires (220ms
       delay), the headline runs a 60-100ms tense-and-release beat:
       scale 1 → 0.97 (pull-back, 25% mark) → 1.02 (overshoot, 60%) →
       1.0 (settle). The shimmer then lands as the "release". This
       reads as "the celebration beat IS about to drop" — Disney
       ANTICIPATION + EXAGGERATION + FOLLOW-THROUGH on one keyframe.
       The keyframe owns the entire overshoot shape, so its easing is
       the monotonic `--ease-out` (NOT a spring) — see the double-
       overshoot fix note on the `animation:` shorthand below.
       Plays alongside the shimmer (both share `gold-headline-shimmer`
       on `animation:` above); composed via the multi-animation
       shorthand below. */
    transform-origin: center center;
    will-change: transform, background-position;

    /* Golden treatment for the headline word — the unified gold-glow
       celebration (D8 + D9). Reads the shared `--color-gold` canon
       (`styles/tokens.css`), the same family the gold `<CompleteBadge>`
       mark uses. */
    background: linear-gradient(
        100deg,
        var(--color-gold-dark) 0%,
        var(--color-gold) 30%,
        var(--color-gold-light) 46%,
        var(--color-gold) 60%,
        var(--color-gold-dark) 100%
    );
    background-size: 240% 100%;
    background-position: 120% 0;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    /* Establish a stacking context so the glyph-glow `::before` sits
       cleanly behind the fill. */
    position: relative;
    isolation: isolate;
    /* D9 — one-shot gold-shimmer + W12-β anticipation tick. The
       shimmer is the celebratory release (`--motion-duration-complete-
       shimmer` ≈2.4s after the `--motion-delay-complete-shimmer` ≈220ms
       lead-in); the anticipation runs INSIDE the 220ms delay window so
       the headline tenses up then releases right as the shimmer fires.
       Composed as a TWO-ANIMATION shorthand on the same element — both
       declarations are independent; CSS handles their parallel timing.
       PRM bracket retires both below. */
    animation:
        gold-headline-shimmer var(--motion-duration-complete-shimmer)
            var(--ease-out, ease-out) var(--motion-delay-complete-shimmer) both,
        /* W2-close DOUBLE-OVERSHOOT FIX — the anticipate keyframe ALREADY
           overshoots (scale 0.97 → 1.02 → 1.0), so its easing must be a
           monotonic arrival curve, NOT a spring. With `--spring-bouncy`
           now retuned to +20.5% overshoot, layering it over a keyframe
           that already overshoots compounded into a visible DOUBLE-bounce
           on the 4rem headline. `--ease-out` interpolates the keyframe's
           own three-beat shape cleanly so the entrance reads as a single
           settle — the keyframe supplies all the spring character; the
           easing's only job is to ride between its stops. */
        complete-headline-anticipate 220ms var(--ease-out, ease-out) 0ms both;
}

/* D8 — glyph-clipped headline glow. The `::before` re-renders the word
   as a second `background-clip: text` layer, blurred, so the glow IS
   the blurred silhouette of the letterforms — it hugs the glyphs
   instead of blooming as a rectangle behind them. Inherits the same
   gold gradient + shimmer animation so the glow shimmers in lockstep
   with the fill. */
.gold-headline::before {
    content: attr(data-text);
    position: absolute;
    inset: 0;
    z-index: -1;
    background: inherit;
    background-size: inherit;
    background-position: inherit;
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: blur(0.5rem);
    opacity: 0.55;
    pointer-events: none;
    animation: inherit;
}

/* The `gold-headline-shimmer` + `complete-headline-anticipate` keyframes
   are hoisted to the central registry at `@design/animations.css` (W3.b);
   the two-animation shorthand above resolves to those global names. */

/* PRM bracket — the D9 gold-shimmer + W12-β anticipation tick both
   collapse. The shimmer animation retires (`animation: none`),
   `background-position` pins to a centred resting frame, and the
   anticipation scale retires too (`transform: none`) so the headline
   reads as a static warm gold — the celebratory FILL is preserved,
   only the sweeping/tensing MOTION is removed. The glyph-clipped glow
   `::before` stays painted (it is a static halo, not motion). */
@media (prefers-reduced-motion: reduce) {
    .gold-headline,
    .gold-headline::before {
        animation: none;
        background-position: 50% 0;
        transform: none;
    }
}
</style>
